// Inbound deliveries from the recycler's four other suppliers (the hotel,
// hospital campus, mall and schools of the shared story). The outlet's pickup
// orders cover only part of what a recycler this size receives, so supply-target
// fulfilment counts these on top of the completed outlet pickups. They are
// deterministic per calendar day (rngFor('market:deliveries:<date>')), so a past
// day's numbers never change, and only deliveries already made by "now" count.

import {
  DAY_MS,
  DEMO_MATERIAL_IDS,
  HOUR_MS,
  daysAgo,
  ids,
  isoDate,
  mondayOf,
  monthStart,
  round,
  rngFor,
} from '../core';

export interface SupplierDelivery {
  supplier_id: string;
  material_id: string;
  delivered_at: number;
  kg: number;
}

/** [supplier n, [material code, average kg per working day][], works on Fri/Sat?] */
type SupplierMix = [number, Array<[string, number]>, boolean];

// Sized so that, with the outlet's pickups, a typical week lands near: OCC 85%,
// PET just over target, HDPE 65%, aluminium 90%, steel 50%, mixed paper 80%.
const SUPPLIER_MIX: SupplierMix[] = [
  // Al Faisaliah Hotel: kitchens, minibars, banqueting.
  [2, [['occ', 330], ['pet', 240], ['aluminium_cans', 130], ['clear_glass', 70], ['uco', 35]], true],
  // King Fahd Medical City campus: admin offices, stores, estates workshop.
  [3, [['occ', 480], ['mixed_paper', 300], ['office_paper', 60], ['hdpe', 130], ['steel_scrap', 150], ['copper', 4]], true],
  // Granada Mall: tenants' back-of-house and food court.
  [4, [['occ', 560], ['pet', 300], ['aluminium_cans', 95], ['hdpe', 80], ['steel_scrap', 105], ['ldpe_film', 90]], true],
  // Riyadh Schools District: closed Friday and Saturday.
  [5, [['mixed_paper', 420], ['newspaper', 85], ['pet', 170], ['occ', 75]], false],
];

/** KSA weekend: Friday is quiet, Saturday half. */
const WEEKDAY_FACTOR = [1, 1, 1, 1, 1, 0.3, 0.6]; // indexed by getUTCDay (Sun..Sat)

/** Deliveries over the last `days` days up to now (covers this week and this month). */
export function generateSupplierDeliveries(days = 40, now: number = Date.now()): SupplierDelivery[] {
  const out: SupplierDelivery[] = [];
  const periodStarts = new Set([mondayOf(0).getTime(), monthStart(0).getTime()]);
  for (let d = days; d >= 0; d -= 1) {
    const day = daysAgo(d);
    const weekday = day.getUTCDay();
    const rng = rngFor(`market:deliveries:${isoDate(day)}`);
    for (const [supplierN, mix, weekendWork] of SUPPLIER_MIX) {
      const closed = !weekendWork && (weekday === 5 || weekday === 6);
      // Same number of draws whatever the day, so one supplier never reshuffles another.
      const hour = rng.float(5.5, 16, 2);
      for (const [code, mean] of mix) {
        const noise = rng.float(0.55, 1.45, 3);
        const kg = closed ? 0 : round(mean * WEEKDAY_FACTOR[weekday] * noise, 1);
        let at = day.getTime() + hour * HOUR_MS;
        // On the first day of the current week or month, the day's loads are logged as
        // already in (an overnight drop), so a period never opens with nothing received.
        if (at > now && periodStarts.has(day.getTime()) && now - day.getTime() < DAY_MS) {
          at = Math.max(day.getTime(), now);
        }
        if (kg <= 0 || at > now) continue;
        out.push({ supplier_id: ids.supplier(supplierN), material_id: DEMO_MATERIAL_IDS[code], delivered_at: at, kg });
      }
    }
  }
  return out;
}

/** Received kg per material id in [start, end). */
export function supplierReceivedKg(startMs: number, endMs: number, now: number = Date.now()): Record<string, number> {
  const days = Math.ceil((now - startMs) / DAY_MS) + 1;
  const totals: Record<string, number> = {};
  for (const d of generateSupplierDeliveries(Math.max(0, days), now)) {
    if (d.delivered_at < startMs || d.delivered_at >= endMs) continue;
    totals[d.material_id] = (totals[d.material_id] ?? 0) + d.kg;
  }
  return totals;
}
